import memoizeOne from 'memoize-one';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { UserProductGroup } from '~/Api/User/ProductGroup';
import { fetchRecentGroups, Filter, paginatedFetchGroups, selectGroups, selectRecent } from '~/ProductGroup';
import { ApplicationState, ThunkDispatch } from '~/Store';

import GroupSelect, { type SelectedOption } from './GroupSelect';

const memoizedSelectGroups = memoizeOne(selectGroups);
const memoizedSelectSubGroups = memoizeOne((state, parentId) => {
	return parentId
		? selectGroups(state, new Filter(parentId, null))
		: { status: 'not-fetched', pagination: null, groups: [] };
});
const memoizedSelectRecent = memoizeOne(selectRecent);
const selectFilter = memoizeOne((parentId, nameFilter) => {
	return new Filter(parentId, Array.from(nameFilter).length >= 3 ? nameFilter : null);
});

const mapStateToProps = (
	state: ApplicationState,
	props: {
		selectedOption: SelectedOption | null;
		nameFilter: string;
	}
): {
	isFetching: boolean;
	isFetchingSubGroups: boolean;
	hasMore: boolean;
	groups: UserProductGroup[];
	subGroups: UserProductGroup[];
	recentGroups: UserProductGroup[];
	hasMoreSubGroups: boolean;
	canCreateGroup: boolean;
} => {
	const { selectedOption } = props;
	const { accessToken } = state.accessToken;

	const groupsState = memoizedSelectGroups(state.productGroup, selectFilter(null, props.nameFilter));
	const subGroupsState = memoizedSelectSubGroups(
		state.productGroup,
		selectedOption && selectedOption.parentValue ? selectedOption.parentValue.id : null
	);

	return {
		isFetching: state.accessToken.isFetching || groupsState.status === 'fetching',
		hasMore: groupsState.pagination?.hasNextPage() ?? false,
		groups: groupsState.groups,
		recentGroups: memoizedSelectRecent(state.productGroup),
		isFetchingSubGroups: subGroupsState.status === 'fetching',
		hasMoreSubGroups:
			subGroupsState.status === 'not-fetched' || !subGroupsState.pagination || subGroupsState.pagination.hasNextPage(),
		subGroups: subGroupsState.groups,
		canCreateGroup: accessToken !== null,
	};
};

const actions = {
	onLoad: () => (dispatch: ThunkDispatch<AnyAction>) => {
		dispatch(paginatedFetchGroups(selectFilter(null, '')));
		dispatch(fetchRecentGroups());
	},
	onFetch: (parentId: string | null, nameFilter: string) => (dispatch: ThunkDispatch<AnyAction>) => {
		dispatch(paginatedFetchGroups(selectFilter(parentId, nameFilter)));
	},
};

export default connect(mapStateToProps, actions)(GroupSelect);
